import { z } from "zod";
import { defineStepConfig } from "../../registry";
import { conditionalSocket } from "../../types";

const inputSchema = z
  .object({
    approverEmails: z
      .array(z.string())
      .optional()
      .describe(
        "Email addresses of approvers to notify. Required when notifyApprovers is enabled in config.",
      ),
    draftSalesOrderId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "ID of the draft sales order under review. Used when inputType is 'sales_order'; rendered as a rich artifact in the approver's inbox.",
      ),
  })
  .passthrough();

const outputSchema = z.object({
  // Widened additively: existing workflows branch on approved/rejected and keep
  // matching; timed_out/escalated are new terminal decisions for team flows.
  // "pending" is the event_driven terminal: the step created a task + ended the
  // run without taking any routable socket; it resumes via a fresh run when the
  // task is completed (TASK_COMPLETED re-trigger).
  decision: z.enum([
    "approved",
    "rejected",
    "timed_out",
    "escalated",
    "pending",
  ]),
  decidedBy: z.string().uuid().nullable(),
  decidedAt: z.string(),
  comment: z.string().nullable(),
  // Echoes the sales-order subject (from input.draftSalesOrderId) ONLY when the
  // decision is "approved", so an approved draft can route straight to the
  // submit step. On reject/timeout/escalate the socket is not taken, and skip
  // propagation stops the approved-path downstream steps for that item.
  approvedDraftSalesOrderId: conditionalSocket(z.string().uuid())
    .optional()
    .describe(
      "The approved draft sales order id, forwarded to downstream submit only when the decision is approved",
    ),
  // Mirror of approvedDraftSalesOrderId for the reject outcome: taken only when
  // the decision is "rejected", so the canvas can route rejected drafts (e.g.
  // back to a fix/re-evaluate breakpoint) without affecting the approved path.
  rejected: conditionalSocket(z.string().uuid())
    .optional()
    .describe(
      "The rejected draft sales order id, taken only when the decision is rejected",
    ),
});

/** A single field/operator/value condition (rule-evaluator compatible). */
const ApprovalConditionSchema = z.object({
  field: z.string(),
  operator: z.enum([
    "eq",
    "neq",
    "gt",
    "gte",
    "lt",
    "lte",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "is_null",
    "is_not_null",
    "in",
    "not_in",
    "regex_match",
  ]),
  value: z.unknown().optional(),
});

/** One condition group — ALL conditions must match (AND). */
const ApprovalConditionGroupSchema = z.object({
  conditions: z.array(ApprovalConditionSchema),
});

const configSchema = z.object({
  inputType: z
    .enum(["document", "text", "sales_order"])
    .default("document")
    .describe("The type of input the approver will review"),
  // Execution model:
  //  - "blocking" (default): the step pauses the run on a Temporal signal until a
  //    decision arrives (back-compatible; used by every existing workflow).
  //  - "event_driven": the step does NOT pause. If a decision already exists for
  //    (subject, approvalKey) it routes on it; otherwise it raises one task,
  //    notifies once, and ENDS the run. Completing the task records the decision
  //    and emits TASK_COMPLETED, which re-triggers the (event-driven) workflow to
  //    re-read the decision and continue. For the OMS v3 approval-chain workflow.
  executionMode: z
    .enum(["blocking", "event_driven"])
    .default("blocking")
    .describe(
      "blocking = pause/Temporal-wait for a decision; event_driven = create a task + end the run, resume via a fresh run on TASK_COMPLETED.",
    ),
  // Stable key identifying THIS approval gate's decision on the subject draft, so
  // a fresh event_driven run reads the prior decision. Defaults to the step id.
  approvalKey: z
    .string()
    .optional()
    .describe(
      "Stable key for this approval's decision on the subject (event_driven mode). Defaults to the step id.",
    ),
  // OR of groups, AND within a group (mirrors the Filter Rules builder). When
  // absent/empty the step always applies; when present and the order's facts
  // match no group, the step auto-passes (emits the approved subject) without
  // notifying or waiting — so a chain of conditional Approval steps lets each
  // gate apply only to the orders it targets.
  appliesWhen: z
    .array(ApprovalConditionGroupSchema)
    .optional()
    .describe(
      "Condition groups (OR of groups, AND within) over the order facts; the step only requires approval when the facts match. Empty/absent = always applies.",
    ),
  // Each named escalation path adds an output socket the approver can route to
  // (e.g. 'vp_review'), letting the canvas wire a follow-up Approval step. An
  // optional per-path `appliesWhen` condition lets a path AUTO-route on the
  // order's facts (e.g. total >= 100k → 'exec_review') without manual selection.
  escalationPaths: z
    .array(
      z.object({
        name: z
          .string()
          .regex(
            /^[a-z][a-z0-9_]*$/,
            "Escalation path name must be a valid socket id (lowercase, underscores)",
          ),
        label: z.string().optional(),
        appliesWhen: z
          .array(ApprovalConditionGroupSchema)
          .optional()
          .describe(
            "Condition groups (OR of groups, AND within) over the order facts; when exactly one path's condition matches, the step auto-escalates to it. Paths without a condition are manual-only.",
          ),
      }),
    )
    .optional()
    .describe(
      "Named escalation outcomes; each adds an output socket the approver can route to (manually) or auto-route to (via appliesWhen).",
    ),
  approverType: z
    .enum(["user", "team"])
    .default("user")
    .describe(
      "Whether the approver is a single user (approverUserId) or an organizational team (approverTeamId). Defaults to 'user' for backward compatibility.",
    ),
  approverTeamId: z
    .string()
    .uuid()
    .optional()
    .describe(
      "Organizational team (um_user_groups) whose members are the approvers. Required when approverType is 'team'. Every member is notified; any member (or their out-of-office delegate) may decide. Escalation is composed on the canvas as a chain of Approval steps.",
    ),
  timeoutSeconds: z
    .number()
    .int()
    .positive()
    .optional()
    .describe(
      "If set, the step auto-completes with decision 'timed_out' when no decision is made within this many seconds, letting the canvas route to an escalation path.",
    ),
  instructions: z
    .string()
    .default("")
    .describe("Instructions or context displayed to the approver"),
  notifyApprovers: z
    .boolean()
    .default(false)
    .describe(
      "When true, send an email notification to approvers when the step enters review state",
    ),
  senderMailbox: z
    .string()
    .optional()
    .describe(
      "The mailbox UPN (e.g. 'orders@company.com') to send notifications from via Microsoft Graph. Required when notifyApprovers is true.",
    ),
  emailSubject: z
    .string()
    .optional()
    .describe(
      "Custom email subject line. Defaults to 'Approval requested — {workflow name}'",
    ),
  // Reference to the email template used for the approver-notification body.
  // Either a built-in (`builtin:<key>`) or an org email-template lineage id
  // (uuid). Relaxed from .uuid() to carry the `builtin:` prefix. When unset, the
  // step renders the legacy built-in approval-request body (back-compatible).
  emailTemplateId: z
    .string()
    .optional()
    .describe(
      "Email template for the approver notification body — a built-in (`builtin:<key>`) or an org email-template id. Unset = built-in approval-request default.",
    ),
  approverUserId: z
    .string()
    .uuid()
    .optional()
    .describe(
      "User ID of the approver to notify. Resolved to their current email at runtime.",
    ),
});

const emits = [] as const;

export const approvalStep = defineStepConfig({
  type: "approval",
  name: "Approval",
  description: "Pause workflow for human approval before continuing",
  category: "Control Flow",
  inputSchema,
  outputSchema,
  configSchema,
  emits,
});
